export type TravelerToolboxCategory =
  | "monde"
  | "autos"
  | "experiences"
  | "esim";

export type KiwiDeepLinkInput = {
  from: string;
  to: string;
  departure?: string;
  returnDate?: string;
};

export const TRAVELER_TOOLBOX_LINKS = Object.freeze({
  kiwiClickDomain: "https://c111.travelpayouts.com/click",
  kiwiMarker: "705546.explorer_plus_flights",
  kiwiPromoId: "3791",
  kiwiSourceType: "customlink",
  kiwiType: "click",
  qeeqDirect: "https://www.qeeq.com/",
  klookDirect: "https://www.klook.com/",
  airaloAffiliate:
    "https://tp.media/r?marker=705546&trs=501285&p=8310&u=https%3A%2F%2Fwww.airalo.com%2F&campaign_id=541",
});

const TOOLBOX_CATEGORIES = new Set<TravelerToolboxCategory>([
  "monde",
  "autos",
  "experiences",
  "esim",
]);

function normalizeAirport(value: string): string {
  return value.trim().toUpperCase();
}

function normalizeDate(value?: string): string | undefined {
  const normalized = value?.trim();
  return normalized || undefined;
}

export function normalizeTravelerToolboxCategory(
  value: unknown,
): TravelerToolboxCategory | null {
  if (typeof value !== "string") {
    return null;
  }

  const normalized = value.trim().toLowerCase();

  if (TOOLBOX_CATEGORIES.has(normalized as TravelerToolboxCategory)) {
    return normalized as TravelerToolboxCategory;
  }

  return null;
}

export function buildKiwiAffiliateUrl(
  input: KiwiDeepLinkInput,
): string {
  const from = normalizeAirport(input.from);
  const to = normalizeAirport(input.to);

  if (!from || !to) {
    throw new Error("Les aéroports de départ et d’arrivée sont requis.");
  }

  const kiwiDeepLink = new URL("https://www.kiwi.com/deep");
  kiwiDeepLink.searchParams.set("from", from);
  kiwiDeepLink.searchParams.set("to", to);

  const departure = normalizeDate(input.departure);
  const returnDate = normalizeDate(input.returnDate);

  if (departure) {
    kiwiDeepLink.searchParams.set("departure", departure);
  }

  if (returnDate) {
    kiwiDeepLink.searchParams.set("return", returnDate);
  }

  const affiliateUrl = new URL(TRAVELER_TOOLBOX_LINKS.kiwiClickDomain);
  affiliateUrl.searchParams.set("shmarker", TRAVELER_TOOLBOX_LINKS.kiwiMarker);
  affiliateUrl.searchParams.set("promo_id", TRAVELER_TOOLBOX_LINKS.kiwiPromoId);
  affiliateUrl.searchParams.set("source_type", TRAVELER_TOOLBOX_LINKS.kiwiSourceType);
  affiliateUrl.searchParams.set("type", TRAVELER_TOOLBOX_LINKS.kiwiType);
  affiliateUrl.searchParams.set("custom_url", kiwiDeepLink.toString());

  return affiliateUrl.toString();
}

export function validateAiraloAffiliateUrl(value: string): boolean {
  let parsed: URL;

  try {
    parsed = new URL(value);
  } catch {
    return false;
  }

  const validEndpoint =
    (parsed.hostname === "tp.media" && parsed.pathname === "/r") ||
    (parsed.hostname === "emrldco.com" && parsed.pathname === "/re");

  if (!validEndpoint) {
    return false;
  }

  if (
    parsed.searchParams.get("marker") !== "705546" ||
    parsed.searchParams.get("trs") !== "501285" ||
    parsed.searchParams.get("p") !== "8310" ||
    parsed.searchParams.get("campaign_id") !== "541"
  ) {
    return false;
  }

  const target = parsed.searchParams.get("u");

  if (!target) {
    return false;
  }

  try {
    const targetUrl = new URL(target);

    return (
      targetUrl.protocol === "https:" &&
      (
        targetUrl.hostname === "airalo.com" ||
        targetUrl.hostname.endsWith(".airalo.com")
      )
    );
  } catch {
    return false;
  }
}

export type TravelerToolboxWidget = {
  id: string;
  src: string;
  title: string;
  minHeight: number;
};

export type TravelerToolboxWidgetMap = {
  monde: TravelerToolboxWidget;
  autos: TravelerToolboxWidget;
  experiences: TravelerToolboxWidget;
  esim: TravelerToolboxWidget;
};

export const TRAVELER_TOOLBOX_WIDGETS: TravelerToolboxWidgetMap = Object.freeze({
  monde: { id: "toolbox-flights", src: "/affiliate-widgets/toolbox-flights.html", title: "Comparateur de vols partenaire", minHeight: 520 },
  autos: { id: "toolbox-cars", src: "/affiliate-widgets/toolbox-cars.html", title: "Comparateur de location de voitures partenaire", minHeight: 420 },
  experiences: { id: "toolbox-experiences", src: "/affiliate-widgets/toolbox-experiences.html", title: "Comparateur activites partenaire", minHeight: 400 },
  esim: { id: "toolbox-esim", src: "/affiliate-widgets/toolbox-esim.html", title: "Comparateur eSIM partenaire", minHeight: 420 },
});
